import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleInfo,
  faCirclePlay,
  faDownload,
  faFileLines,
  faPenCircle,
} from '@fortawesome/pro-light-svg-icons';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import moment from 'moment';

// utils
import { formatNumToPhone } from '../../../../utils/format-tel';
import forceDownload from '../../../records/force-download';

// Components
import { Table } from '../../../../common/components';
import Pagination from './Pagination';
import TableFilter from './TableFilter';
import { loadThreeWayCalls, updateThreeWayCall } from '../../../../actions/three_way';

function ThreeWay({ user = {}, modal = false }) {
  const dispatch = useDispatch();
  const [editRow, setEditRow] = useState({});
  const [audioRow, setAudioRow] = useState({});
  const { callData = {}, sortTerm, sortInverse } = useSelector(store => store.threeWay);
  const { Items: callBook = [] } = callData;

  useEffect(() => {
    dispatch({ type: 'CLEAR_THREE_WAY' });
    loadThreeWayCalls(user.username).then(config => {
      dispatch({ type: 'SET_THREE_WAY_FIELD_VALUE', field: 'callData', payload: config });
    });
  }, []);

  function handleSort(key, direction) {
    let callBookCopy = [ ...callBook ];
    if (key === 'call_duration' || key === 'shopper_phone') {
      if (direction === 'asc') {
        callBookCopy = callBookCopy.sort((a, b) => {
          if (a[key] < b[key]) return -1;
          if (a[key] > b[key]) return 1;
          return 0;
        });
      } else if (direction === 'desc') {
        callBookCopy = callBookCopy.sort((a, b) => {
          if (b[key] < a[key]) return -1;
          if (b[key] > a[key]) return 1;
          return 0;
        });
      }
    } else if (key === 'call_start') {
      if (direction === 'asc') {
        callBookCopy = callBookCopy.sort((a, b) => new Date(a[key]) - new Date(b[key]));
      } else if (direction === 'desc') {
        callBookCopy = callBookCopy.sort((a, b) => new Date(b[key]) - new Date(a[key]));
      }
    } else if (direction === 'asc') {
      callBookCopy = callBookCopy.sort((a, b) => a[key].localeCompare(b[key]));
    } else if (direction === 'desc') {
      callBookCopy = callBookCopy.sort((a, b) => b[key].localeCompare(a[key]));
    }
    dispatch({ type: 'SET_CALL_BOOK', payload: callBookCopy });
  }

  function handleCallDetails(data) {
    dispatch({ type: 'OPEN_MODAL', modal: 'threeWayCallDetails' });
    dispatch({ type: 'SET_CALL_DETAILS_DATA', payload: data });
  }

  function handleEdit(event) {
    const { id, value } = event.target;
    const editRowCopy = { ...editRow };
    editRowCopy[id] = value;
    setEditRow(editRowCopy);
  }


  function handleSave() {
    updateThreeWayCall(editRow)
      .then(() => {
        dispatch({ type: 'CLEAR_THREE_WAY' });
        loadThreeWayCalls(user.username).then(config => {
          dispatch({ type: 'SET_THREE_WAY_FIELD_VALUE', field: 'callData', payload: config });
        });
      });
    setEditRow({});
  }

  // cell formatting
  const formatFirstNameCell = (data) => {
    if (data.call_id === editRow.call_id) {
      return (
        <input
          type="text"
          id="shopper_first_name"
          className="form-control"
          value={editRow.shopper_first_name}
          onChange={(e) => handleEdit(e)}
        />
      );
    }
    return data.shopper_first_name;
  };
  const formatLastNameCell = (data) => {
    if (data.call_id === editRow.call_id) {
      return (
        <input
          type="text"
          id="shopper_last_name"
          className="form-control"
          value={editRow.shopper_last_name}
          onChange={(e) => handleEdit(e)}
        />
      );
    }
    return data.shopper_last_name;
  };
  const formatPhoneCell = (data) => {
    if (data.call_id === editRow.call_id) {
      return (
        <input
          type="text"
          id="shopper_phone"
          className="form-control"
          value={editRow.shopper_phone}
          onChange={(e) => handleEdit(e)}
        />
      );
    }
    return formatNumToPhone(data.shopper_phone);
  };
  const formatTimeCell = (time) => `${moment(time).format('M/D/YY')} at ${moment(time).format('h:mma')}`;
  const formatDurationCell = (duration) => moment.utc(duration * 1000).format('HH:mm:ss');
  const formatCallToolsCell = (data) => {
    if (data.call_id === editRow.call_id) {
      return (
        <>
          <button
            type="button"
            className="btn btn-success mx-1"
            onClick={() => handleSave()}
          >
            Save
          </button>
          <button
            type="button"
            className="btn btn-danger mx-1"
            onClick={() => setEditRow({})}
          >
            Cancel
          </button>
        </>
      );
    }
    if (data.call_id === audioRow.call_id) {
      return (
        <div className="d-flex justify-content-center">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio controls>
            <source src={data.recording_url} />
          </audio>
          <button
            type="button"
            className="btn ms-1"
            onClick={() => setAudioRow({})}
          >
            <FontAwesomeIcon icon={faCircleXmark} color="#d65656" size="xl" />
          </button>
        </div>
      );
    }
    return (
      <>
        <button
          type="button"
          className="btn"
          title="Call details"
          onClick={() => handleCallDetails(data)}
        >
          <FontAwesomeIcon icon={faCircleInfo} size="lg" color="#5b67ba" />
        </button>
        <button
          type="button"
          className="btn call-tools"
          title="Play call audio"
          disabled={!data.recording_url}
          onClick={() => setAudioRow(data)}
        >
          <FontAwesomeIcon
            icon={faCirclePlay}
            size="lg"
            color={!data.recording_url ? '#707070' : '#5b67ba'}
          />
        </button>
        <button
          type="button"
          className="btn call-tools"
          title="Download call audio"
          disabled={!data.recording_url || !data.recording_s3_key}
          onClick={() => forceDownload(data.recording_url, data.recording_s3_key)}
        >
          <FontAwesomeIcon
            icon={faDownload}
            size="lg"
            color={!data.recording_url || !data.recording_s3_key ? '#707070' : '#5b67ba'}
          />
        </button>
        <button
          type="button"
          className="btn call-tools"
          title="Download call transcript"
          disabled={!data.transcript_url || !data.transcript_s3_key}
          onClick={() => forceDownload(data.transcript_url, data.transcript_s3_key)}
        >
          <FontAwesomeIcon
            icon={faFileLines}
            size="lg"
            color={!data.transcript_url || !data.transcript_s3_key ? '#707070' : '#5b67ba'}
          />
        </button>
        <button
          type="button"
          className="btn"
          title="Edit client name or phone number"
          onClick={() => setEditRow(data)}
        >
          <FontAwesomeIcon
            icon={faPenCircle}
            size="lg"
            color="#5b67ba"
          />
        </button>
      </>
    );
  };

  const schema = {
    cols: [
      {
        header: 'Client first name',
        passAllData: true,
        sortKey: 'shopper_first_name',
        alphaSortIcon: true,
        formatter: formatFirstNameCell,
      },
      {
        header: 'Client Last name',
        passAllData: true,
        sortKey: 'shopper_last_name',
        alphaSortIcon: true,
        formatter: formatLastNameCell,
      },
      {
        header: 'Client phone number',
        formatter: formatPhoneCell,
        passAllData: true,
        sortKey: 'shopper_phone',
      },
      {
        header: 'Date Time',
        field: 'call_start',
        sortKey: 'call_start',
        formatter: formatTimeCell,
      },
      {
        header: 'Duration',
        formatter: formatDurationCell,
        field: 'call_duration',
        sortKey: 'call_duration',
      },
      {
        header: 'Call Tools',
        passAllData: true,
        formatter: formatCallToolsCell,
      },
    ],
    sortClickCallback: (key, direction) => handleSort(key, direction),
    sortKeySetter: (sk) => dispatch({ type: 'SET_THREE_WAY_SORT_TERM', payload: sk }),
    sortKeyGetter: () => sortTerm,
    sortDirectionSetter: (sd) => dispatch({ type: 'SET_THREE_WAY_SORT_INVERSE', payload: sd }),
    sortDirectionGetter: () => sortInverse,
  };

  return (
    <div className="d-flex justify-content-center">
      <div className="col">
        <TableFilter user={user} modal={modal} />
        <Table
          schema={schema}
          data={callBook}
          className="table table-bordered text-center three-way"
        />
        <div className="d-flex justify-content-center">
          <Pagination user={user} />
        </div>
      </div>
    </div>
  );
}

export default ThreeWay;
